export const getComplexName = (complex) => {
  if (!complex) return "";
  if (complex?.name) return complex.name;
  if (complex?.exercises?.length > 0) {
    let name = "";
    complex.exercises.forEach((exercise, index) => {
      if (index > 0) name += " + ";
      if (exercise.movement) {
        name += exercise.movement + " ";
      }
      let primary = exercise.primary;
      if (primary === "overheadPress") primary = "Overhead Press";
      if (
        !["clean", "jerk", "snatch", "other"].includes(primary) ||
        !exercise.movement
      ) {
        name += primary.charAt(0).toUpperCase() + primary.slice(1);
      }
    });
    return name;
  }
  return "";
};

export const getInitialValue = (exercise) => {
  if (!exercise) return null;
  const { primary, movement } = exercise;
  const formattedMovement = primary.charAt(0).toUpperCase() + primary.slice(1);
  const formatted = ["other", "clean", "jerk", "snatch"].includes(primary)
    ? ""
    : ` (${formattedMovement})`;
  if (!movement) {
    return {
      label: formattedMovement,
      value: primary,
      primary,
      secondary: "",
    };
  }
  return {
    label: movement + formatted,
    value: `${primary}${movement}`,
    primary,
    secondary: movement,
  };
};

export const getComplexExerciseName = (complex, index) => {
  if (!complex?.exercises?.length) return "";

  const exercise = complex.exercises[index ?? complex.loadBasedOn ?? 0];
  if (!exercise) return "";

  let { movement, primary } = exercise;
  if (primary === "overheadPress") primary = "Overhead Press";
  if (!movement) {
    return primary.charAt(0).toUpperCase() + primary.slice(1);
  }
  if (["clean", "jerk", "snatcb", "other"].includes(primary)) {
    return movement;
  }
  return `${movement} ${primary.charAt(0).toUpperCase()}${primary.slice(1)}`;
};

export const groupExercisesByComplex = (exercises = []) => {
  const result = [];
  const usedIds = new Set();

  for (const ex of exercises) {
    if (usedIds.has(ex._id)) continue;

    if (ex.complex && ex.complexInstance) {
      const complexGroup = exercises.filter(
        (e) =>
          e.complex === ex.complex &&
          e.complexInstance === ex.complexInstance &&
          !usedIds.has(e._id)
      );

      complexGroup.forEach((e) => usedIds.add(e._id));

      result.push({
        complex: ex.complex,
        complexName: ex.complexName,
        complexInstance: ex.complexInstance,
        exercises: complexGroup,
        ...complexGroup[0],
      });
    } else {
      result.push(ex);
      usedIds.add(ex._id);
    }
  }

  return result;
};

export const getComplexSetReps = (exercises, setIndex) => {
  let reps = "";
  exercises.forEach((exercise, index) => {
    const set = exercise.sets[setIndex];
    if (set) {
      if (index > 0 && index < exercises.length) reps += "+";
      reps += set.reps;
    }
  });
  return reps ? `(${reps})` : "";
};

export const getComplexSets = (list, exerciseIds) => {
  if (!exerciseIds?.length || !list?.length) return [];
  const exercises = list.filter((ex) => exerciseIds.includes(ex._id));
  if (!exercises[0]?.sets?.length) return [];
  return exercises[0].sets.map((set, index) => {
    const fullSet = { ...set };
    if (exercises[1]?.sets[index]?.reps) {
      fullSet.repsTwo = exercises[1].sets[index].reps;
    }
    if (exercises[2]?.sets[index]?.reps) {
      fullSet.repsThree = exercises[2].sets[index].reps;
    }
    return fullSet;
  });
};

export const getComplexOptions = (complexes) => {
  return complexes.map((complex) => {
    return {
      label: complex.name,
      value: complex.name,
      complex: complex._id,
    };
  });
};

export const getComplexSetOutcomeReps = (exercise, setIndex, outcomeIndex) => {
  let repsTwo, repsThree;
  if (exercise.exercises?.length) {
    repsTwo =
      exercise.exercises?.[1]?.sets?.[setIndex]?.outcomes?.[outcomeIndex]
        ?.outcome?.reps;
    repsThree =
      exercise.exercises?.[2]?.sets?.[setIndex]?.outcomes?.[outcomeIndex]
        ?.outcome?.reps;
  }
  return { repsTwo, repsThree };
};

/**
 * Gets the rep maximum for an exercise, accounting for complex loadBasedOn logic
 * @param {Object} exercise - Exercise object
 * @param {Object} user - User object with repMaximums and exerciseLibrary
 * @param {Array} complexes - Array of complex documents
 * @returns {number} Rep maximum to use for load calculation
 */
export const getRepMaxForExercise = (exercise, user, complexes) => {
  // If exercise is not part of a complex, use standard logic
  if (!exercise.complex) {
    return getStandardRepMax(exercise, user);
  }

  // Find the complex document
  const complex = complexes.find((c) => c._id === exercise.complex);
  if (
    !complex ||
    !complex.exercises ||
    !complex.exercises[complex.loadBasedOn]
  ) {
    return getStandardRepMax(exercise, user);
  }

  // Get the loadBasedOn exercise info
  const loadBasedOnExercise = complex.exercises[complex.loadBasedOn];

  // Use the loadBasedOn exercise to determine rep maximum
  return getRepMaxForExerciseInfo(loadBasedOnExercise, user);
};

/**
 * Gets rep maximum using standard (non-complex) logic
 * @param {Object} exercise - Exercise object
 * @param {Object} user - User object with repMaximums and exerciseLibrary
 * @returns {number} Rep maximum
 */
const getStandardRepMax = (exercise, user) => {
  if (exercise.type === "primary") {
    return user.repMaximums[exercise.primary] || 100;
  } else {
    // Secondary exercise logic
    if (!user.exerciseLibrary || !user.exerciseLibrary[exercise.primary]) {
      return user.repMaximums[exercise.primary] || 100;
    }

    const secondaryExercise = user.exerciseLibrary[exercise.primary].find(
      ({ movement }) => movement === exercise.secondary
    );

    if (secondaryExercise && secondaryExercise.repMaximum) {
      return secondaryExercise.repMaximum;
    }

    return user.repMaximums[exercise.primary] || 100;
  }
};

/**
 * Gets rep maximum for exercise info from complex.exercises array
 * @param {Object} exerciseInfo - Exercise info from complex.exercises array
 * @param {Object} user - User object with repMaximums and exerciseLibrary
 * @returns {number} Rep maximum
 */
const getRepMaxForExerciseInfo = (exerciseInfo, user) => {
  // If exercise has movement (secondary), look in exerciseLibrary
  if (exerciseInfo.movement) {
    if (user.exerciseLibrary && user.exerciseLibrary[exerciseInfo.primary]) {
      const secondaryExercise = user.exerciseLibrary[exerciseInfo.primary].find(
        ({ movement }) => movement === exerciseInfo.movement
      );

      if (secondaryExercise && secondaryExercise.repMaximum) {
        return secondaryExercise.repMaximum;
      }
    }
  }

  // Fall back to primary rep maximum
  return user.repMaximums[exerciseInfo.primary] || 100;
};
